import React, { useEffect, useState, useRef } from "react";
import {
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  TextField,
  Button,
  Box,
  IconButton,
  MenuItem,
  Select,
  Typography,
  Chip,
  CircularProgress,
  Collapse,
} from "@mui/material";

import CloseIcon from "@mui/icons-material/Close";
import AttachFileIcon from "@mui/icons-material/AttachFile";
import KeyboardArrowDownIcon from "@mui/icons-material/KeyboardArrowDown";

import DropZoneWrapper from "../dropwrapper/DropZoneWrapper";
import RecipientField from "../RecipientField";
import {
  resolveSignatureHtmlForCompose,
  signatureHtmlForSend,
} from "../signatureHtml";
import {
  composeDialogSlotProps,
  guardComposeDialogClose,
  hasComposeDraft,
  ComposeDiscardConfirmDialog,
} from "../composeDialogUi";

import axios from "axios";
import TipTapEditor from "./TipTapEditor";
import { validateRecipientField } from "../emailRecipients";
import { safeSetEditorHtml } from "../tiptapExtensions";
import {
  quotedOriginalPreviewSx,
} from "../quotedOriginalHtml";

const API = process.env.REACT_APP_API_URL;

const ACCENT = "#00bbcd";
const ACCENT_HOVER = "#00a5b5";
const ON_ACCENT = "#0f172a";

const accentBtnSx = {
  bgcolor: ACCENT,
  color: ON_ACCENT,
  fontWeight: 600,
  boxShadow: "none",
  "&:hover": { bgcolor: ACCENT_HOVER, boxShadow: "none" },
};

const accentOutlinedSx = {
  color: ACCENT,
  borderColor: ACCENT,
  "&:hover": {
    borderColor: ACCENT_HOVER,
    bgcolor: "rgba(0, 187, 205, 0.08)",
  },
};

const accentTextSx = {
  color: ACCENT,
  "&:hover": { bgcolor: "rgba(0, 187, 205, 0.08)" },
};

export default function ForwardDialogPro({
  open,
  onClose,
  onSend,
  fromEmail: fromEmailProp,
  accounts = [],
  email,
  forwardedHtml,
  forwardOriginalAttachments,
}) {
  const editorRef = useRef(null);
  const forwardedHtmlRef = useRef("");
  const draftBaselineRef = useRef(null);

  const [discardOpen, setDiscardOpen] = useState(false);

  const [fromEmail, setFromEmail] = useState("");
  const [to, setTo] = useState("");
  const [cc, setCc] = useState("");
  const [bcc, setBcc] = useState("");
  const [showCc, setShowCc] = useState(false);
  const [showBcc, setShowBcc] = useState(false);
  const [subject, setSubject] = useState("");

  const [selectedSignature, setSelectedSignature] = useState("main");
  const [signatures, setSignatures] = useState({ main: "", reply: "", logo_path: null });

  const [initialHtml, setInitialHtml] = useState("");

  /* 🔥 NUEVO → adjuntos */
  const [attachments, setAttachments] = useState([]);
  const [originalAttachments, setOriginalAttachments] = useState([]);
  const [isSending, setIsSending] = useState(false);

  const accountOptions = (accounts || [])
    .map((a) => ({
      id: a?.id,
      email: String(a?.email || "").trim(),
      value: String(a?.email || "").trim().toLowerCase(),
    }))
    .filter((a) => a.value);

  const selectFromEmail = accountOptions.some((a) => a.value === fromEmail)
    ? fromEmail
    : accountOptions.some((a) => a.value === String(fromEmailProp || "").toLowerCase())
      ? String(fromEmailProp || "").toLowerCase()
      : accountOptions[0]?.value || "";

  const requestClose = () => {
    const baseline = draftBaselineRef.current || {};
    if (
      hasComposeDraft({
        to,
        cc,
        bcc,
        subject,
        html: editorRef.current?.getHTML?.() ?? initialHtml,
        attachments,
        baseline,
      })
    ) {
      setDiscardOpen(true);
      return;
    }
    onClose();
  };

  const confirmDiscard = () => {
    setDiscardOpen(false);
    onClose();
  };

  useEffect(() => {
    if (!open) {
      draftBaselineRef.current = null;
    }
  }, [open]);

  const handleFileChange = (e) => {
    const files = Array.from(e.target.files);
    setAttachments((prev) => [...prev, ...files]);
  };

  const removeAttachment = (name) => {
    setAttachments((prev) => prev.filter((f) => f.name !== name));
  };

  function decodeBase64Safe(b64) {
    if (!b64 || typeof b64 !== "string") return null;

    // Gmail usa base64 URL-SAFE
    let base64 = b64.replace(/-/g, "+").replace(/_/g, "/");

    // Padding
    const padding = 4 - (base64.length % 4);
    if (padding !== 4) {
      base64 += "=".repeat(padding);
    }

    try {
      const raw = atob(base64);
      return Uint8Array.from(raw, (c) => c.charCodeAt(0));
    } catch (err) {
      console.error("Base64 decode ERROR:", err);
      return null;
    }
  }

  useEffect(() => {
    if (!open) return;

    setTo("");
    setCc("");
    setBcc("");
    setShowCc(false);
    setShowBcc(false);
    setSubject(email ? `Fwd: ${email.subject || ""}` : "");
    setAttachments([]);

    const options = (accounts || [])
      .map((a) => String(a?.email || "").trim().toLowerCase())
      .filter(Boolean);
    const preferred = String(fromEmailProp || "").trim().toLowerCase();
    setFromEmail(
      (preferred && options.includes(preferred) && preferred) ||
        options[0] ||
        ""
    );

    if (Array.isArray(forwardOriginalAttachments) && forwardOriginalAttachments.length > 0) {
      try {
        const converted = forwardOriginalAttachments
          .map((a, idx) => {
            try {
              const arr = decodeBase64Safe(a?.dataBase64);
              if (!arr) return null;

              return new File([arr], a.filename || `archivo-${idx + 1}`, {
                type: a.mimeType || "application/octet-stream",
              });
            } catch (err) {
              console.warn("Adjunto original omitido:", err);
              return null;
            }
          })
          .filter(Boolean);

        setOriginalAttachments(converted);
      } catch (err) {
        console.warn("No se pudieron convertir adjuntos originales:", err);
        setOriginalAttachments([]);
      }
    } else {
      setOriginalAttachments([]);
    }

    draftBaselineRef.current = {
      to: "",
      cc: "",
      bcc: "",
      subject: email ? `Fwd: ${email.subject || ""}` : "",
      html: draftBaselineRef.current?.html ?? "",
    };
  }, [open, email, forwardOriginalAttachments, fromEmailProp, accounts]);

  /* Cargar firmas */
  useEffect(() => {
    if (!open) return;
    const emailForSig = fromEmail || selectFromEmail;
    if (!emailForSig) return;

    axios
      .get(`${API}/signatures/${emailForSig}`)
      .then((res) => {
        const main = res.data.signature_html || "";
        const reply = res.data.reply_signature_html || main;

        setSignatures({
          main,
          reply,
          logo_path: res.data.logo_path || null,
        });
      })
      .catch((err) => {
        console.warn("⚠️ firmas reenviar:", err);
        setSignatures({ main: "", reply: "", logo_path: null });
      });
  }, [open, fromEmail, selectFromEmail]);

  useEffect(() => {
    forwardedHtmlRef.current = forwardedHtml || "";
  }, [forwardedHtml]);

  /* Solo la firma va al editor; el bloque reenviado se preserva sin pasar por TipTap */
  useEffect(() => {
    if (!open) return;

    const sig = resolveSignatureHtmlForCompose(
      selectedSignature === "main"
        ? signatures.main
        : selectedSignature === "reply"
        ? signatures.reply
        : "",
      signatures.logo_path,
      API
    );

    draftBaselineRef.current = {
      ...(draftBaselineRef.current || {}),
      html: sig,
    };

    if (!editorRef.current) {
      setInitialHtml(sig);
    } else {
      safeSetEditorHtml(editorRef.current, sig);
    }
  }, [open, signatures, selectedSignature]);

  /* Enviar email reenviado */
  const sendForward = async () => {
    if (isSending) return;
    const sendAs = fromEmail || selectFromEmail;
    if (!sendAs) {
      alert("Selecciona la cuenta desde la que reenviar.");
      return;
    }
    if (!to.trim()) {
      alert("Debe indicar al menos un destinatario.");
      return;
    }

    const recipients = validateRecipientField(to);
    if (!recipients.ok) {
      alert(`❌ ${recipients.message}`);
      return;
    }
    const ccCheck = validateRecipientField(cc, { required: false });
    if (!ccCheck.ok) {
      alert(`❌ ${ccCheck.message}`);
      return;
    }
    const bccCheck = validateRecipientField(bcc, { required: false });
    if (!bccCheck.ok) {
      alert(`❌ ${bccCheck.message}`);
      return;
    }

    const sigHtml = signatureHtmlForSend(
      editorRef.current?.getHTML?.() || "",
      signatures.logo_path,
      API
    );

    const form = new FormData();
    form.append("fromEmail", sendAs);
    form.append("to", recipients.normalized);
    form.append("cc", ccCheck.normalized);
    form.append("bcc", bccCheck.normalized);
    form.append("subject", subject);
    form.append("html", sigHtml);
    form.append("skipServerSignature", "true");
    if (email?.gmailId) {
      form.append("forwardSourceGmailId", email.gmailId);
    }

    attachments.forEach((file) => form.append("attachments", file));
    originalAttachments
      .filter((f) => !String(f.type || "").startsWith("image/"))
      .forEach((file) => form.append("attachments", file));

    setIsSending(true);
    try {
      await onSend(form);
      onClose();
    } catch (err) {
      console.error("Error reenviando:", err);
      const msg =
        err?.response?.data?.error ||
        err?.message ||
        "Error reenviando el correo";
      alert(`❌ ${msg}`);
    } finally {
      setIsSending(false);
    }
  };

  return (
    <>
    <Dialog
      open={open}
      maxWidth="md"
      fullWidth
      onClose={isSending ? undefined : guardComposeDialogClose(requestClose)}
      disableEscapeKeyDown
      slotProps={composeDialogSlotProps}
    >
      <DialogTitle sx={{ display: "flex", justifyContent: "space-between" }}>
        Reenviar mensaje
        <IconButton onClick={requestClose} disabled={isSending}>
          <CloseIcon />
        </IconButton>
      </DialogTitle>

      <DialogContent sx={{ px: 3, py: 2 }}>
        <Select
          fullWidth
          size="small"
          sx={{ mb: 2 }}
          displayEmpty
          value={selectFromEmail}
          onChange={(e) => setFromEmail(e.target.value)}
        >
          {!accountOptions.length ? (
            <MenuItem value="" disabled>
              Sin cuentas
            </MenuItem>
          ) : null}
          {accountOptions.map((acc) => (
            <MenuItem key={acc.id || acc.value} value={acc.value}>
              {acc.email}
            </MenuItem>
          ))}
        </Select>

        {/* PARA */}
        <RecipientField
          label="Para"
          value={to}
          onChange={setTo}
          disabled={isSending}
          sx={{ mb: 1 }}
        />

        <Box sx={{ display: "flex", gap: 1, mb: 1 }}>
          <Button
            size="small"
            onClick={() => setShowCc((x) => !x)}
            endIcon={<KeyboardArrowDownIcon />}
            sx={accentTextSx}
          >
            CC
          </Button>
          <Button
            size="small"
            onClick={() => setShowBcc((x) => !x)}
            endIcon={<KeyboardArrowDownIcon />}
            sx={accentTextSx}
          >
            CCO
          </Button>
        </Box>

        <Collapse in={showCc}>
          <RecipientField
            label="CC"
            value={cc}
            onChange={setCc}
            disabled={isSending}
          />
        </Collapse>

        <Collapse in={showBcc}>
          <RecipientField
            label="CCO"
            value={bcc}
            onChange={setBcc}
            disabled={isSending}
          />
        </Collapse>

        {/* ASUNTO */}
        <TextField
          fullWidth
          size="small"
          label="Asunto"
          value={subject}
          onChange={(e) => setSubject(e.target.value)}
          sx={{ mb: 2 }}
        />

        {/* SELECT FIRMA */}
        <Select
          fullWidth
          size="small"
          value={selectedSignature}
          onChange={(e) => setSelectedSignature(e.target.value)}
          sx={{ mb: 2 }}
        >
          <MenuItem value="main">Firma principal</MenuItem>
          <MenuItem value="reply">Firma respuesta</MenuItem>
          <MenuItem value="none">Sin firma</MenuItem>
        </Select>

        {/* 🔥 Botón adjuntar */}
        <Button
          variant="outlined"
          size="small"
          startIcon={<AttachFileIcon />}
          component="label"
          sx={{ mb: 1, ...accentOutlinedSx }}
        >
          Adjuntar archivos
          <input type="file" hidden multiple onChange={handleFileChange} />
        </Button>
        {/* 🔥 Adjuntos ORIGINALES */}
        {originalAttachments.length > 0 && (
          <Box sx={{ mt: 1, mb: 1, display: "flex", gap: 1, flexWrap: "wrap" }}>
            {originalAttachments.map((file, idx) => (
              <Chip
                key={`${file.name}-${idx}`}
                label={`📎 ${file.name}`}
                onDelete={() =>
                  setOriginalAttachments((prev) => prev.filter((f) => f !== file))
                }
                sx={{ fontSize: 12 }}
              />
            ))}
          </Box>
        )}

        {/* 🔥 Mostrar chips */}
        {attachments.length > 0 && (
          <Box sx={{ mt: 1, mb: 2, display: "flex", gap: 1, flexWrap: "wrap" }}>
            {attachments.map((file, idx) => (
              <Chip
                key={`${file.name}-${idx}`}
                label={
                  String(file.type || "").startsWith("image/")
                    ? `🖼️ ${file.name}`
                    : `📄 ${file.name}`
                }
                onDelete={() => removeAttachment(file.name)}
                sx={{ fontSize: 12 }}
              />
            ))}
          </Box>
        )}

        {/* Firma / texto nuevo (editable) */}
        <DropZoneWrapper
          onFiles={(files) => setAttachments((prev) => [...prev, ...files])}
        >
          <TipTapEditor
            editorRef={editorRef}
            initialHtml={initialHtml}
            onChange={() => {}}
          />
        </DropZoneWrapper>

        {/* Mensaje reenviado intacto (tablas, imágenes inline, etc.) */}
        {forwardedHtml ? (
          <Box
            sx={quotedOriginalPreviewSx}
            dangerouslySetInnerHTML={{ __html: forwardedHtml }}
          />
        ) : (
          <Typography sx={{ color: "#777", mt: 2 }}>Cargando mensaje original…</Typography>
        )}
      </DialogContent>

      <DialogActions>
        <Button onClick={requestClose} disabled={isSending} sx={accentTextSx}>
          Cancelar
        </Button>
        <Button
          variant="contained"
          onClick={sendForward}
          disabled={isSending}
          startIcon={
            isSending ? <CircularProgress size={16} color="inherit" /> : null
          }
          sx={accentBtnSx}
        >
          {isSending ? "Enviando..." : "Enviar"}
        </Button>
      </DialogActions>
    </Dialog>

    <ComposeDiscardConfirmDialog
      open={discardOpen}
      onCancel={() => setDiscardOpen(false)}
      onConfirm={confirmDiscard}
    />
    </>
  );
}
